/**
 * PermissionDebugPanel — Debug why navigation items are missing.
 *
 * Shows ALL possible nav items and routes, not just allowed ones.
 * For each item, shows:
 *   - ✅ Enabled — you have access
 *   - ⚠️ Missing Scope — shows which scope you need
 *   - 🔒 Wrong Layer — shows required layer
 *   - 🚫 Feature Disabled — tenant feature flag is off
 *
 * Toggle with Ctrl+Shift+D or via the pill in bottom-left.
 *
 * Architecture:
 *   - Reads ALL items from useTenantSubgraphConfig (unfiltered)
 *   - Reads ALLOWED items from allowedNavigation query
 *   - Diffs to show what's missing and why
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  ActionIcon,
  Tooltip,
  ScrollArea,
  TextInput,
  SegmentedControl,
  ThemeIcon,
  Divider,
  Alert,
  Kbd,
  Table,
  Button,
  CopyButton,
} from '@mantine/core';
import {
  IconBug,
  IconCheck,
  IconAlertTriangle,
  IconLock,
  IconX,
  IconSearch,
  IconFlag,
  IconCopy,
} from '@tabler/icons-react';

import { useAuth, useViewLayer } from '@/auth/AuthContext';
import { useTenantSubgraphConfig, type SubgraphNavItem, type SubgraphRoute } from '@/hooks/useTenantSubgraphConfig';
import { useTenantFeatureFlags, type TenantFeatureFlags } from '@ferp/workflow-forms';
import { useNavigation } from '@/contexts/NavigationContext';
import { isDevModeEnabled } from '@/utils/env';

// ── Types ─────────────────────────────────────────────────────────────────────

type ItemStatus = 'enabled' | 'missing_scope' | 'wrong_layer' | 'feature_disabled';

interface DebugItem {
  path: string;
  label: string;
  status: ItemStatus;
  reason?: string;
  requiredScope?: string;
  requiredLayer?: string;
  featureFlag?: string;
  subgraph?: string;
}

// ── Subgraph detection from path ──────────────────────────────────────────────

function getSubgraphFromPath(path: string): string {
  if (path.startsWith('/admin/fhir')) return 'fhir';
  if (path.startsWith('/admin/payroll')) return 'payroll';
  if (path.startsWith('/admin/erp')) return 'erp';
  if (path.startsWith('/ferp-core')) return 'core';
  if (path.startsWith('/primitives')) return 'core';
  if (path.startsWith('/architect')) return 'core';
  if (path.startsWith('/admin')) return 'core';
  if (path.startsWith('/exchange')) return 'edi';
  if (path.startsWith('/sync')) return 'core';
  if (path.startsWith('/alerts')) return 'core';
  return 'core';
}

const SUBGRAPH_COLORS: Record<string, string> = {
  core: 'violet',
  fhir: 'blue',
  payroll: 'yellow',
  erp: 'green',
  edi: 'orange',
};

// ── Feature flag to path mapping (mirror of useFeatureFilteredConfig) ─────────

const FEATURE_FLAG_PATHS: { flag: keyof TenantFeatureFlags; paths: string[] }[] = [
  { flag: 'careJourney', paths: ['/ferp-core/journey'] },
  { flag: 'beaconDashboard', paths: ['/alerts/beacon'] },
  { flag: 'witnessQueue', paths: ['/admin/witness-queue'] },
  { flag: 'standardsPack', paths: ['/ferp-core/compliance/standards-packs'] },
  { flag: 'syncDashboard', paths: ['/sync'] },
];

function getFeatureFlagForPath(path: string): keyof TenantFeatureFlags | null {
  for (const { flag, paths } of FEATURE_FLAG_PATHS) {
    if (paths.includes(path)) return flag;
  }
  return null;
}

// ── Layer hierarchy (ARCHITECT > ADMIN > USER) ────────────────────────────────

const LAYER_RANK: Record<string, number> = {
  user: 1,
  admin: 2,
  architect: 3,
};

function canAccessLayer(userLayer: string, requiredLayer: string): boolean {
  const userRank = LAYER_RANK[userLayer.toLowerCase()] ?? 0;
  const requiredRank = LAYER_RANK[requiredLayer.toLowerCase()] ?? 0;
  return userRank >= requiredRank;
}

// ── Status colors and icons ───────────────────────────────────────────────────

const STATUS_CONFIG: Record<ItemStatus, { color: string; icon: React.ReactNode; label: string }> = {
  enabled: { color: 'green', icon: <IconCheck size={14} />, label: 'Enabled' },
  missing_scope: { color: 'orange', icon: <IconAlertTriangle size={14} />, label: 'Missing Scope' },
  wrong_layer: { color: 'red', icon: <IconLock size={14} />, label: 'Wrong Layer' },
  feature_disabled: { color: 'gray', icon: <IconFlag size={14} />, label: 'Feature Off' },
};

// ── Component ─────────────────────────────────────────────────────────────────

export function PermissionDebugPanel() {
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'missing'>('missing');

  const { tenant } = useAuth();
  const viewLayer = useViewLayer();
  const tenantId = tenant?.id ?? null;

  // Get ALL nav items (unfiltered)
  const allConfig = useTenantSubgraphConfig(tenantId);
  const allNavItems = allConfig.config?.allNavItems ?? [];
  const allRoutes = allConfig.config?.allRoutes ?? [];

  // DRR-313: Use shared NavigationContext for allowed navigation
  const { allowedNavigation, loading } = useNavigation();

  // Get feature flags
  const { flags: featureFlags } = useTenantFeatureFlags();

  // Build allowed sets for quick lookup
  const allowedPaths = useMemo(() => {
    const routes = allowedNavigation?.routes ?? [];
    const navItems = allowedNavigation?.navItems ?? [];
    const paths = new Set<string>();
    for (const r of routes) paths.add(r.path);
    for (const n of navItems) paths.add(n.path);
    return paths;
  }, [allowedNavigation]);

  // Build debug items
  const debugItems = useMemo((): DebugItem[] => {
    const items: DebugItem[] = [];
    const seen = new Set<string>();

    // Process nav items
    for (const item of allNavItems) {
      if (!item.path || seen.has(item.path)) continue;
      seen.add(item.path);

      const isAllowed = allowedPaths.has(item.path);
      const featureFlag = getFeatureFlagForPath(item.path);
      const featureDisabled = featureFlag && !featureFlags[featureFlag];

      let status: ItemStatus = 'enabled';
      let reason: string | undefined;
      let requiredScope: string | undefined;
      let requiredLayer: string | undefined;

      if (!isAllowed) {
        if (featureDisabled) {
          status = 'feature_disabled';
          reason = `Feature flag "${featureFlag}" is disabled for this tenant`;
        } else if (item.layer && !canAccessLayer(viewLayer, item.layer)) {
          status = 'wrong_layer';
          reason = `Requires "${item.layer}" layer, you have "${viewLayer}"`;
          requiredLayer = item.layer;
        } else {
          status = 'missing_scope';
          // Try to extract required scope from item metadata
          // DRR-102: routes have requiredScopes field
          const route = allRoutes.find((r: SubgraphRoute) => r.path === item.path);
          if (route?.requiredPermissions?.length) {
            requiredScope = route.requiredPermissions.join(', ');
            reason = `Missing scope: ${requiredScope}`;
          } else {
            reason = 'Missing required scope (check backend RequireScope guard)';
          }
        }
      }

      items.push({
        path: item.path,
        label: item.label || item.path,
        status,
        reason,
        requiredScope,
        requiredLayer,
        featureFlag: featureFlag ?? undefined,
        subgraph: getSubgraphFromPath(item.path),
      });
    }

    // Sort: missing first, then alphabetical
    items.sort((a, b) => {
      if (a.status === 'enabled' && b.status !== 'enabled') return 1;
      if (a.status !== 'enabled' && b.status === 'enabled') return -1;
      return a.label.localeCompare(b.label);
    });

    return items;
  }, [allNavItems, allRoutes, allowedPaths, viewLayer, featureFlags]);

  // Filter items
  const filteredItems = useMemo(() => {
    let items = debugItems;

    // Status filter
    if (statusFilter === 'missing') {
      items = items.filter(i => i.status !== 'enabled');
    }

    // Text filter
    if (filter) {
      const lowerFilter = filter.toLowerCase();
      items = items.filter(
        i => i.path.toLowerCase().includes(lowerFilter) ||
             i.label.toLowerCase().includes(lowerFilter) ||
             i.requiredScope?.toLowerCase().includes(lowerFilter)
      );
    }

    return items;
  }, [debugItems, statusFilter, filter]);

  // Stats
  const stats = useMemo(() => {
    const total = debugItems.length;
    const enabled = debugItems.filter(i => i.status === 'enabled').length;
    const missingScope = debugItems.filter(i => i.status === 'missing_scope').length;
    const wrongLayer = debugItems.filter(i => i.status === 'wrong_layer').length;
    const featureOff = debugItems.filter(i => i.status === 'feature_disabled').length;
    return { total, enabled, missingScope, wrongLayer, featureOff };
  }, [debugItems]);

  // Build copy-able JSON
  const debugJson = useMemo(() => JSON.stringify({
    timestamp: new Date().toISOString(),
    viewLayer,
    tenant: tenant?.name ?? null,
    stats,
    items: debugItems.map(i => ({
      path: i.path,
      label: i.label,
      subgraph: i.subgraph ?? null,
      status: i.status,
      reason: i.reason ?? null,
      requiredScope: i.requiredScope ?? null,
      requiredLayer: i.requiredLayer ?? null,
    })),
  }, null, 2), [viewLayer, tenant, stats, debugItems]);

  // Keyboard shortcut: Ctrl+Shift+D
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setExpanded(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ── Minimized pill ──────────────────────────────────────────────────────────

  if (!expanded) {
    return (
      <Tooltip label={<>Permission Debug <Kbd size="xs">Ctrl+Shift+D</Kbd></>}>
        <Paper
          shadow="md"
          radius="xl"
          style={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            zIndex: 1100,
            cursor: 'pointer',
            padding: '6px 14px',
          }}
          onClick={() => setExpanded(true)}
        >
          <Group gap="xs">
            <ThemeIcon size="xs" radius="xl" color="orange" variant="filled">
              <IconBug size={10} />
            </ThemeIcon>
            <Text size="xs" fw={600}>Debug</Text>
            {stats.missingScope + stats.wrongLayer > 0 && (
              <Badge size="xs" color="orange">
                {stats.missingScope + stats.wrongLayer} blocked
              </Badge>
            )}
          </Group>
        </Paper>
      </Tooltip>
    );
  }

  // ── Expanded panel ──────────────────────────────────────────────────────────

  return (
    <Paper
      shadow="xl"
      radius="md"
      p="md"
      withBorder
      style={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        width: 500,
        maxHeight: 'calc(100vh - 100px)',
        zIndex: 1100,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap="sm" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon size="sm" color="orange" variant="light">
              <IconBug size={14} />
            </ThemeIcon>
            <Text size="sm" fw={600}>Permission Debug</Text>
            <Kbd size="xs">Ctrl+Shift+D</Kbd>
          </Group>
          <Group gap="xs">
            <CopyButton value={debugJson}>
              {({ copied, copy }) => (
                <Button
                  size="xs"
                  variant={copied ? 'filled' : 'light'}
                  color={copied ? 'teal' : 'gray'}
                  leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  onClick={copy}
                >
                  {copied ? 'Copied!' : 'Copy JSON'}
                </Button>
              )}
            </CopyButton>
            <ActionIcon variant="subtle" size="sm" onClick={() => setExpanded(false)}>
              <IconX size={14} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Stats */}
        <Group gap="xs">
          <Badge size="sm" color="green" variant="light">{stats.enabled} enabled</Badge>
          <Badge size="sm" color="orange" variant="light">{stats.missingScope} missing scope</Badge>
          <Badge size="sm" color="red" variant="light">{stats.wrongLayer} wrong layer</Badge>
          <Badge size="sm" color="gray" variant="light">{stats.featureOff} feature off</Badge>
        </Group>

        <Divider />

        {/* Filters */}
        <Group gap="xs">
          <TextInput
            size="xs"
            placeholder="Search path, label, or scope..."
            leftSection={<IconSearch size={14} />}
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <SegmentedControl
            size="xs"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as 'all' | 'missing')}
            data={[
              { label: 'Missing', value: 'missing' },
              { label: 'All', value: 'all' },
            ]}
          />
        </Group>

        {/* Current context */}
        <Alert color="blue" variant="light" p="xs">
          <Group gap="xs">
            <Text size="xs">View Layer:</Text>
            <Badge size="xs" color="blue">{viewLayer}</Badge>
            <Text size="xs" c="dimmed">|</Text>
            <Text size="xs">Tenant:</Text>
            <Badge size="xs" variant="outline">{tenant?.name ?? 'none'}</Badge>
          </Group>
        </Alert>

        {/* Items list */}
        <ScrollArea h={350} offsetScrollbars type="always" style={{ border: '1px solid var(--mantine-color-dark-4)', borderRadius: 4 }}>
            {loading ? (
              <Text size="sm" c="dimmed" ta="center" py="md">Loading...</Text>
            ) : filteredItems.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">
                {statusFilter === 'missing' ? 'No missing permissions!' : 'No items found'}
              </Text>
            ) : (
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 30 }}></Table.Th>
                    <Table.Th>Path / Label</Table.Th>
                    <Table.Th>Why Blocked</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredItems.map((item) => {
                    const config = STATUS_CONFIG[item.status];
                    return (
                      <Table.Tr key={item.path}>
                        <Table.Td>
                          <ThemeIcon size="sm" color={config.color} variant="light">
                            {config.icon}
                          </ThemeIcon>
                        </Table.Td>
                        <Table.Td>
                          <Stack gap={2}>
                            <Group gap={4}>
                              <Text size="xs" fw={500}>{item.label}</Text>
                              {item.subgraph && (
                                <Badge size="xs" variant="dot" color={SUBGRAPH_COLORS[item.subgraph] ?? 'gray'}>
                                  {item.subgraph}
                                </Badge>
                              )}
                            </Group>
                            <Text size="xs" c="dimmed" ff="monospace">{item.path}</Text>
                          </Stack>
                        </Table.Td>
                        <Table.Td>
                          {item.status === 'enabled' ? (
                            <Badge size="xs" color="green" variant="light">Allowed</Badge>
                          ) : (
                            <Stack gap={2}>
                              <Badge size="xs" color={config.color} variant="filled">
                                {config.label}
                              </Badge>
                              {item.requiredScope && (
                                <Text size="xs" c="dimmed" ff="monospace">
                                  Need: {item.requiredScope}
                                </Text>
                              )}
                              {item.requiredLayer && (
                                <Text size="xs" c="dimmed">
                                  Need layer: {item.requiredLayer}
                                </Text>
                              )}
                              {item.featureFlag && (
                                <Text size="xs" c="dimmed">
                                  Flag: {item.featureFlag}
                                </Text>
                              )}
                            </Stack>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            )}
          </ScrollArea>

        {/* Help text */}
        <Text size="xs" c="dimmed">
          Shows all {stats.total} nav items. Green = visible. Orange/Red = blocked (see reason).
        </Text>
      </Stack>
    </Paper>
  );
}
